// Mock data for development — images from picsum.photos
// Each dream has themed images to make the app feel alive

import type { Dream, ViewerData } from './api';

// Curated picsum images that look dreamy/aspirational
const dreamImages = {
  travel: [
    'https://picsum.photos/id/164/1080/1920', // ocean sunset
    'https://picsum.photos/id/165/1080/1920', // tropical
    'https://picsum.photos/id/197/1080/1920', // waterfall
    'https://picsum.photos/id/173/1080/1920', // mountain
    'https://picsum.photos/id/182/1080/1920', // golden hour
    'https://picsum.photos/id/206/1080/1920', // beach
    'https://picsum.photos/id/238/1080/1920', // hills
    'https://picsum.photos/id/240/1080/1920', // coast
  ],
  nature: [
    'https://picsum.photos/id/10/1080/1920',  // forest
    'https://picsum.photos/id/15/1080/1920',  // river
    'https://picsum.photos/id/16/1080/1920',  // ocean rocks
    'https://picsum.photos/id/28/1080/1920',  // mountains
    'https://picsum.photos/id/29/1080/1920',  // snowy peak
    'https://picsum.photos/id/47/1080/1920',  // lake
    'https://picsum.photos/id/54/1080/1920',  // autumn
    'https://picsum.photos/id/56/1080/1920',  // sky
  ],
  luxury: [
    'https://picsum.photos/id/36/1080/1920',  // interior
    'https://picsum.photos/id/42/1080/1920',  // architecture
    'https://picsum.photos/id/65/1080/1920',  // modern
    'https://picsum.photos/id/84/1080/1920',  // city
    'https://picsum.photos/id/119/1080/1920', // villa
    'https://picsum.photos/id/164/1080/1920', // sunset
    'https://picsum.photos/id/188/1080/1920', // horizon
    'https://picsum.photos/id/274/1080/1920', // design
  ],
  wellness: [
    'https://picsum.photos/id/106/1080/1920', // zen
    'https://picsum.photos/id/129/1080/1920', // serene
    'https://picsum.photos/id/134/1080/1920', // green
    'https://picsum.photos/id/142/1080/1920', // garden
    'https://picsum.photos/id/167/1080/1920', // peaceful
    'https://picsum.photos/id/177/1080/1920', // flowers
    'https://picsum.photos/id/192/1080/1920', // organic
    'https://picsum.photos/id/210/1080/1920', // calm
  ],
};

// Thumbnails (square crops)
const thumb = (id: number) => `https://picsum.photos/id/${id}/400/400`;

export const mockDreams: Dream[] = [
  {
    id: 'mock-1',
    title: 'Voyage au Japon',
    description: 'Me voir explorer les temples de Kyoto au coucher du soleil, marcher dans les ruelles fleuries, vivre ce moment de paix absolue.',
    rejectText: null,
    status: 'active',
    isActive: true,
    palette: null,
    backgroundAssetId: null,
    thumbnailUrl: thumb(164),
    imagesCount: 8,
    markersCount: 3,
    createdAt: '2026-02-01T10:00:00Z',
    realizedAt: null,
  },
  {
    id: 'mock-2',
    title: 'Ma maison de reve',
    description: 'Une villa lumineuse avec vue sur la mer, un jardin luxuriant, une cuisine ouverte baignee de soleil.',
    rejectText: null,
    status: 'active',
    isActive: false,
    palette: null,
    backgroundAssetId: null,
    thumbnailUrl: thumb(36),
    imagesCount: 8,
    markersCount: 1,
    createdAt: '2026-01-28T14:00:00Z',
    realizedAt: null,
  },
  {
    id: 'mock-3',
    title: 'Serenite interieure',
    description: 'Trouver la paix interieure, mediter dans un endroit magnifique, me sentir en harmonie avec la nature.',
    rejectText: null,
    status: 'inactive',
    isActive: false,
    palette: null,
    backgroundAssetId: null,
    thumbnailUrl: thumb(106),
    imagesCount: 8,
    markersCount: 0,
    createdAt: '2026-01-20T09:00:00Z',
    realizedAt: null,
  },
  {
    id: 'mock-4',
    title: 'Decouverte de la nature',
    description: 'Randonner dans les plus beaux paysages du monde, sentir la liberte au sommet des montagnes.',
    rejectText: null,
    status: 'realized',
    isActive: false,
    palette: null,
    backgroundAssetId: null,
    thumbnailUrl: thumb(29),
    imagesCount: 8,
    markersCount: 5,
    createdAt: '2026-01-10T08:00:00Z',
    realizedAt: '2026-02-05T16:00:00Z',
  },
];

function makeImages(urls: string[]): ViewerData['images'] {
  return urls.map((url, i) => ({
    id: `img-${i}`,
    url,
    width: 1080,
    height: 1920,
    isFavorite: i === 0,
  }));
}

export const mockViewerData: Record<string, ViewerData> = {
  'mock-1': {
    id: 'mock-1',
    title: 'Voyage au Japon',
    palette: null,
    backgroundAssetId: null,
    navigationMode: 'scroll',
    images: makeImages(dreamImages.travel),
    loop: true,
  },
  'mock-2': {
    id: 'mock-2',
    title: 'Ma maison de reve',
    palette: null,
    backgroundAssetId: null,
    navigationMode: 'scroll',
    images: makeImages(dreamImages.luxury),
    loop: true,
  },
  'mock-3': {
    id: 'mock-3',
    title: 'Serenite interieure',
    palette: null,
    backgroundAssetId: null,
    navigationMode: 'scroll',
    images: makeImages(dreamImages.wellness),
    loop: true,
  },
  'mock-4': {
    id: 'mock-4',
    title: 'Decouverte de la nature',
    palette: null,
    backgroundAssetId: null,
    navigationMode: 'scroll',
    images: makeImages(dreamImages.nature),
    loop: true,
  },
};

// Helper: check if we should use mock data (no backend available)
export function useMockMode(): boolean {
  // In dev, always use mocks unless VITE_API_URL is explicitly set
  return !import.meta.env.VITE_API_URL;
}
